import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TablePagination,
  Chip,
  Avatar,
  InputAdornment,
  Checkbox,
  Skeleton,
  TableSortLabel,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  MdDelete,
  MdEdit,
  MdAdd,
  MdSearch,
  MdPersonOutline,
  MdPeople,
  MdAdminPanelSettings,
  MdBadge,
  MdGroups,
  MdGridView,
  MdViewList,
  MdPhone,
  MdEmail,
  MdShield,
  MdRefresh,
  MdClear,
} from "react-icons/md";
import { createUser, deleteUser, getAllUsers, updateUser } from "../apis/userApi";
import toast from "react-hot-toast";
import { brand } from "../theme";

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

const roleChipStyle = (role) => {
  const key = (role || "").toLowerCase();
  if (key === "admin") return { backgroundColor: brand.ink950, color: brand.parchment };
  if (key === "employee") return { backgroundColor: brand.brassSoft, color: brand.ink950 };
  if (key === "vendor") return { backgroundColor: "#8B5CF622", color: "#6D28D9", border: "1px solid #8B5CF644" };
  return { backgroundColor: `${brand.slateLight}40`, color: brand.ink950 };
};

const avatarBg = (role) => {
  const key = (role || "").toLowerCase();
  if (key === "admin") return brand.ink950;
  if (key === "employee") return brand.brass;
  if (key === "vendor") return "#6D28D9";
  return brand.slate;
};

const StatCard = ({ icon, label, count, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "20px",
      display: "flex",
      alignItems: "center",
      gap: 2,
      bgcolor: brand.paper,
      border: `1px solid ${brand.slateLight}40`,
      boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
      transition: "transform 200ms ease, box-shadow 200ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 18px 40px -18px rgba(11,10,61,0.12)",
        borderColor: `${brand.brass}66`,
      },
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `${accent || brand.brass}18`,
        color: accent || brand.ink950,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0}>
      <Typography
        sx={{
          fontFamily: '"Fraunces", serif',
          fontWeight: 700,
          fontSize: "1.8rem",
          color: brand.ink950,
          lineHeight: 1.1,
        }}
      >
        {count}
      </Typography>
      <Typography sx={{ color: brand.slate, fontSize: "0.82rem", fontWeight: 600 }} noWrap>
        {label}
      </Typography>
    </Box>
  </Paper>
);

const User = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [selected, setSelected] = useState([]);
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'grid'

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    mobile: "",
    role: "employee",
    password: "",
    confirmPassword: "",
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await getAllUsers();
      setUsers(Array.isArray(res) ? res : []);
    } catch (err) {
      toast.error("Failed to load users list");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const roles = useMemo(() => {
    const set = new Set(users.map((u) => (u.role || "").toLowerCase()).filter(Boolean));
    return Array.from(set);
  }, [users]);

  const counts = useMemo(() => {
    const byRole = {};
    users.forEach((u) => {
      const key = (u.role || "user").toLowerCase();
      byRole[key] = (byRole[key] || 0) + 1;
    });
    return byRole;
  }, [users]);

  const visibleUsers = useMemo(() => {
    let list = [...users];

    if (roleFilter !== "all") {
      list = list.filter((u) => (u.role || "").toLowerCase() === roleFilter);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (u) =>
          u.name?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term) ||
          u.mobile?.toLowerCase?.().includes(term)
      );
    }

    const { key, direction } = sortConfig;
    list.sort((a, b) => {
      const av = (a[key] || "").toString().toLowerCase();
      const bv = (b[key] || "").toString().toLowerCase();
      if (av < bv) return direction === "asc" ? -1 : 1;
      if (av > bv) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [users, roleFilter, searchTerm, sortConfig]);

  const paginatedUsers = visibleUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const isSelected = (id) => selected.includes(id);

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      const selectableIds = paginatedUsers
        .filter((u) => u.email !== "superadmin@goatform.com")
        .map((u) => u.id);
      setSelected((prev) => Array.from(new Set([...prev, ...selectableIds])));
    } else {
      const pageIds = paginatedUsers.map((u) => u.id);
      setSelected((prev) => prev.filter((id) => !pageIds.includes(id)));
    }
  };

  const toggleSelectOne = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const handleEdit = (user) => {
    setCurrentUser({ ...user });
    setOpenEditDialog(true);
  };

  const performDelete = async (userId) => {
    try {
      await deleteUser(userId);
      setUsers((prev) => prev.filter((user) => user.id !== userId));
      setSelected((prev) => prev.filter((id) => id !== userId));
      toast.success("User deleted successfully");
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to delete user");
    }
  };

  const handleConfirmDelete = async () => {
    const userId = confirmDelete;
    setConfirmDelete(null);
    await performDelete(userId);
  };

  const handleBulkDelete = async () => {
    setConfirmBulkDelete(false);
    const results = await Promise.allSettled(selected.map((id) => deleteUser(id)));
    const failed = results.filter((r) => r.status === "rejected").length;
    setUsers((prev) => prev.filter((u) => !selected.includes(u.id)));
    setSelected([]);
    if (failed > 0) {
      toast.error(`${failed} user(s) could not be deleted`);
    } else {
      toast.success("Selected users deleted successfully");
    }
  };

  const handleSubmitEdit = async () => {
    try {
      await updateUser(currentUser);
      setUsers((prev) => prev.map((user) => (user.id === currentUser.id ? currentUser : user)));
      setOpenEditDialog(false);
      setCurrentUser(null);
      toast.success("User updated successfully");
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to update user");
    }
  };

  const handleAddUser = () => setOpenAddDialog(true);

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitAdd = async () => {
    if (newUser.password && newUser.password !== newUser.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      const userToAdd = await createUser(newUser);
      setUsers((prev) => [...prev, userToAdd || newUser]);
      setOpenAddDialog(false);
      toast.success("User created successfully");
      setNewUser({
        name: "",
        email: "",
        mobile: "",
        role: "employee",
        password: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.error || "Failed to create user");
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pageSelectableIds = paginatedUsers
    .filter((u) => u.email !== "superadmin@goatform.com")
    .map((u) => u.id);
  const allPageSelected = pageSelectableIds.length > 0 && pageSelectableIds.every((id) => selected.includes(id));
  const somePageSelected = pageSelectableIds.some((id) => selected.includes(id));

  return (
    <Box p={{ xs: 2, sm: 3, md: 4 }} sx={{ maxWidth: 1600, margin: "0 auto" }}>
      {/* ── Header Bar ──────────────────────────────────────────────────── */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography
              variant="h3"
              sx={{
                color: brand.ink950,
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              User Management
            </Typography>
            <Chip
              label={`${users.length} Total`}
              size="small"
              sx={{ bgcolor: brand.brassSoft, color: brand.ink950, fontWeight: 700 }}
            />
          </Box>
          <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
            Manage staff accounts, client credentials, and access roles
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <MuiTooltip title="Reload user records">
            <IconButton
              onClick={fetchUsers}
              sx={{
                bgcolor: brand.paper,
                border: `1px solid ${brand.slateLight}60`,
                p: 1.1,
                borderRadius: "14px",
                color: brand.ink950,
                "&:hover": { bgcolor: brand.brassSoft },
              }}
            >
              <MdRefresh size={20} className={loading ? "animate-spin" : ""} />
            </IconButton>
          </MuiTooltip>

          <Button
            variant="contained"
            color="secondary"
            onClick={handleAddUser}
            startIcon={<MdAdd size={20} />}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "0.88rem",
              boxShadow: "0 8px 20px -6px rgba(201,162,39,0.5)",
            }}
          >
            Add New User
          </Button>
        </Box>
      </Box>

      {/* ── Executive Stat Cards ────────────────────────────────────────── */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdPeople size={24} />} label="Total Users" count={users.length} accent="#3B82F6" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdAdminPanelSettings size={24} />}
            label="Administrators"
            count={counts.admin || 0}
            accent={brand.ink950}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdBadge size={24} />} label="Staff & Employees" count={counts.employee || 0} accent={brand.brass} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdGroups size={24} />}
            label="Standard Clients"
            count={counts.user || counts.client || (users.length - (counts.admin || 0) - (counts.employee || 0))}
            accent="#10B981"
          />
        </Grid>
      </Grid>

      {/* ── Filters & Controls Bar ─────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          bgcolor: brand.paper,
          border: `1px solid ${brand.slateLight}40`,
          mb: 3,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          {/* Search Input */}
          <TextField
            placeholder="Search by name, email, phone..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            sx={{
              width: { xs: "100%", md: 320 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: `${brand.slateLight}15`,
                fontSize: "0.85rem",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch color={brand.slate} size={20} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}>
                    <MdClear size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Role Filter Chips & View Mode Toggle */}
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                label={`All (${users.length})`}
                onClick={() => {
                  setRoleFilter("all");
                  setPage(0);
                }}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  px: 0.5,
                  bgcolor: roleFilter === "all" ? brand.ink950 : `${brand.slateLight}25`,
                  color: roleFilter === "all" ? brand.parchment : brand.slate,
                  "&:hover": { bgcolor: roleFilter === "all" ? brand.ink800 : `${brand.slateLight}40` },
                }}
              />
              {["admin", "employee", "user"].map((r) => (
                <Chip
                  key={r}
                  label={`${r.charAt(0).toUpperCase() + r.slice(1)} (${counts[r] || 0})`}
                  onClick={() => {
                    setRoleFilter(r);
                    setPage(0);
                  }}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    px: 0.5,
                    textTransform: "capitalize",
                    bgcolor: roleFilter === r ? brand.brass : `${brand.slateLight}25`,
                    color: roleFilter === r ? brand.ink950 : brand.slate,
                    "&:hover": { bgcolor: roleFilter === r ? brand.brassLight : `${brand.slateLight}40` },
                  }}
                />
              ))}
            </Box>

            {/* View Switcher */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
              sx={{
                bgcolor: `${brand.slateLight}20`,
                p: 0.4,
                borderRadius: "12px",
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "9px",
                  px: 1.5,
                  py: 0.5,
                  color: brand.slate,
                  "&.Mui-selected": {
                    bgcolor: brand.paper,
                    color: brand.ink950,
                    boxShadow: "0 2px 8px rgba(11,10,61,0.08)",
                  },
                },
              }}
            >
              <ToggleButton value="table">
                <MdViewList size={18} />
              </ToggleButton>
              <ToggleButton value="grid">
                <MdGridView size={18} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Paper>

      {/* ── Bulk Actions Bar ───────────────────────────────────────────── */}
      {selected.length > 0 && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            bgcolor: brand.ink950,
            color: brand.parchment,
            borderRadius: "16px",
            px: 3,
            py: 1.5,
            mb: 3,
            boxShadow: "0 14px 30px -10px rgba(11,10,61,0.4)",
          }}
        >
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 700 }}>
            {selected.length} user account(s) selected
          </Typography>
          <Box display="flex" gap={1.5}>
            <Button
              size="small"
              onClick={() => setSelected([])}
              sx={{ color: brand.parchment, opacity: 0.85, fontWeight: 600 }}
            >
              Deselect All
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<MdDelete />}
              onClick={() => setConfirmBulkDelete(true)}
              sx={{ fontWeight: 700, borderRadius: "10px" }}
            >
              Delete Selected
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Data Display: Table vs Grid ────────────────────────────────── */}
      {viewMode === "table" ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "22px",
            overflow: "hidden",
            border: `1px solid ${brand.slateLight}40`,
            bgcolor: brand.paper,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ bgcolor: brand.ink950 }}>
                  <Checkbox
                    checked={allPageSelected}
                    indeterminate={somePageSelected && !allPageSelected}
                    onChange={toggleSelectAll}
                    sx={{ color: brand.parchment, "&.Mui-checked": { color: brand.brassLight } }}
                  />
                </TableCell>
                <TableCell sx={{ color: brand.parchment, fontWeight: 700 }}>S.No</TableCell>
                <TableCell sortDirection={sortConfig.key === "name" ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig.key === "name"}
                    direction={sortConfig.key === "name" ? sortConfig.direction : "asc"}
                    onClick={() => handleSort("name")}
                    sx={{
                      color: `${brand.parchment} !important`,
                      "& .MuiTableSortLabel-icon": { color: `${brand.brassLight} !important` },
                    }}
                  >
                    User Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortConfig.key === "email" ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig.key === "email"}
                    direction={sortConfig.key === "email" ? sortConfig.direction : "asc"}
                    onClick={() => handleSort("email")}
                    sx={{
                      color: `${brand.parchment} !important`,
                      "& .MuiTableSortLabel-icon": { color: `${brand.brassLight} !important` },
                    }}
                  >
                    Email Address
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: brand.parchment, fontWeight: 700 }}>Mobile Contact</TableCell>
                <TableCell sx={{ color: brand.parchment, fontWeight: 700 }}>Access Role</TableCell>
                <TableCell align="right" sx={{ color: brand.parchment, fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedUsers.length > 0 ? (
                paginatedUsers.map((user, index) => {
                  const isProtected = user.email === "superadmin@goatform.com";
                  return (
                    <TableRow
                      key={user.id || user._id || index}
                      selected={isSelected(user.id)}
                      sx={{
                        "&:hover": { bgcolor: `${brand.brassSoft}60` },
                      }}
                    >
                      <TableCell padding="checkbox">
                        {!isProtected && (
                          <Checkbox
                            checked={isSelected(user.id)}
                            onChange={() => toggleSelectOne(user.id)}
                            sx={{ color: brand.slateLight, "&.Mui-checked": { color: brand.brass } }}
                          />
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: brand.slate,
                          fontFamily: '"IBM Plex Mono", monospace',
                          fontSize: "0.82rem",
                          fontWeight: 600,
                        }}
                      >
                        {String(page * rowsPerPage + index + 1).padStart(2, "0")}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              fontSize: "0.85rem",
                              fontWeight: 700,
                              bgcolor: avatarBg(user.role),
                              color: brand.parchment,
                            }}
                          >
                            {initials(user.name) || <MdPersonOutline size={20} />}
                          </Avatar>
                          <Box minWidth={0}>
                            <Box display="flex" alignItems="center" gap={0.8}>
                              <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: brand.ink950 }}>
                                {user.name}
                              </Typography>
                              {isProtected && (
                                <MuiTooltip title="Protected Superadmin Account">
                                  <Box display="inline-flex" color={brand.brass}>
                                    <MdShield size={16} />
                                  </Box>
                                </MuiTooltip>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: brand.slate, fontSize: "0.85rem", fontWeight: 500 }}>
                        {user.email}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: brand.slate,
                          fontSize: "0.85rem",
                          fontFamily: '"IBM Plex Mono", monospace',
                        }}
                      >
                        {user.mobile || "—"}
                      </TableCell>
                      <TableCell>
                        <Chip label={user.role || "User"} size="small" sx={roleChipStyle(user.role)} />
                      </TableCell>
                      <TableCell align="right">
                        {!isProtected && (
                          <Box display="flex" gap={1} justifyContent="flex-end">
                            <MuiTooltip title="Edit User Details">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(user)}
                                sx={{
                                  bgcolor: `${brand.slateLight}25`,
                                  color: brand.ink950,
                                  "&:hover": { bgcolor: brand.brassSoft, color: brand.ink950 },
                                }}
                              >
                                <MdEdit size={16} />
                              </IconButton>
                            </MuiTooltip>
                            <MuiTooltip title="Delete Account">
                              <IconButton
                                size="small"
                                onClick={() => setConfirmDelete(user.id)}
                                sx={{
                                  bgcolor: `${brand.danger}15`,
                                  color: brand.danger,
                                  "&:hover": { bgcolor: brand.danger, color: "#FFFFFF" },
                                }}
                              >
                                <MdDelete size={16} />
                              </IconButton>
                            </MuiTooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <MdPersonOutline size={40} color={brand.slateLight} />
                    <Typography
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: brand.ink950,
                        mt: 1,
                      }}
                    >
                      No user accounts found
                    </Typography>
                    <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
                      {searchTerm || roleFilter !== "all"
                        ? "Try modifying your search or filter keywords."
                        : "Click 'Add New User' to create the first user."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={visibleUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      ) : (
        /* Grid View */
        <Grid container spacing={3} mb={3}>
          {paginatedUsers.map((user) => {
            const isProtected = user.email === "superadmin@goatform.com";
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "22px",
                    bgcolor: brand.paper,
                    border: `1px solid ${brand.slateLight}40`,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    position: "relative",
                    transition: "transform 200ms ease, box-shadow 200ms ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 18px 40px -15px rgba(11,10,61,0.12)",
                      borderColor: `${brand.brass}66`,
                    },
                  }}
                >
                  <Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          fontSize: "1rem",
                          fontWeight: 700,
                          bgcolor: avatarBg(user.role),
                          color: brand.parchment,
                          boxShadow: "0 6px 16px rgba(11,10,61,0.15)",
                        }}
                      >
                        {initials(user.name) || <MdPersonOutline size={24} />}
                      </Avatar>
                      <Chip label={user.role || "User"} size="small" sx={roleChipStyle(user.role)} />
                    </Box>

                    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: brand.ink950 }} noWrap>
                      {user.name}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1} mt={1} color={brand.slate}>
                      <MdEmail size={15} />
                      <Typography sx={{ fontSize: "0.8rem" }} noWrap>
                        {user.email}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1} mt={0.6} color={brand.slate}>
                      <MdPhone size={15} />
                      <Typography sx={{ fontSize: "0.8rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                        {user.mobile || "—"}
                      </Typography>
                    </Box>
                  </Box>

                  {!isProtected && (
                    <Box display="flex" gap={1} justifyContent="flex-end" mt={3} pt={2} borderTop={`1px solid ${brand.slateLight}30`}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleEdit(user)}
                        startIcon={<MdEdit />}
                        sx={{
                          borderRadius: "10px",
                          borderColor: `${brand.slateLight}60`,
                          color: brand.ink950,
                          fontWeight: 600,
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => setConfirmDelete(user.id)}
                        startIcon={<MdDelete />}
                        sx={{ borderRadius: "10px", fontWeight: 600 }}
                      >
                        Delete
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ── Dialog: Edit User ───────────────────────────────────────────── */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        PaperProps={{
          sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 460 },
        }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          Edit User Credentials
        </DialogTitle>
        <DialogContent display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Full Name"
            value={currentUser?.name || ""}
            onChange={(e) => setCurrentUser({ ...currentUser, name: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email Address"
            value={currentUser?.email || ""}
            onChange={(e) => setCurrentUser({ ...currentUser, email: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Mobile Number"
            value={currentUser?.mobile || ""}
            onChange={(e) => setCurrentUser({ ...currentUser, mobile: e.target.value })}
            fullWidth
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Access Role</InputLabel>
            <Select
              value={currentUser?.role || "employee"}
              label="Access Role"
              onChange={(e) => setCurrentUser({ ...currentUser, role: e.target.value })}
            >
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="employee">Staff / Employee</MenuItem>
              <MenuItem value="vendor">Vendor / Supplier</MenuItem>
              <MenuItem value="user">Standard Client</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenEditDialog(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitEdit}
            variant="contained"
            color="secondary"
            sx={{ fontWeight: 700, px: 3, borderRadius: "10px" }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Add User ───────────────────────────────────────────── */}
      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        PaperProps={{
          sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 480 },
        }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          Create New User Account
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Full Name"
            name="name"
            value={newUser.name}
            onChange={handleNewUserChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email Address"
            name="email"
            value={newUser.email}
            onChange={handleNewUserChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Mobile Number"
            name="mobile"
            value={newUser.mobile}
            onChange={handleNewUserChange}
            fullWidth
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Access Role</InputLabel>
            <Select name="role" value={newUser.role} label="Access Role" onChange={handleNewUserChange}>
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="employee">Staff / Employee</MenuItem>
              <MenuItem value="vendor">Vendor / Supplier</MenuItem>
              <MenuItem value="user">Standard Client</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Password"
            name="password"
            type="password"
            value={newUser.password}
            onChange={handleNewUserChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            value={newUser.confirmPassword}
            onChange={handleNewUserChange}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenAddDialog(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitAdd}
            variant="contained"
            color="secondary"
            sx={{ fontWeight: 700, px: 3, borderRadius: "10px" }}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Confirm Single Delete ──────────────────────────────── */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        PaperProps={{ sx: { borderRadius: "20px", p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700 }}>
          Delete User Account?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: brand.slate, fontSize: "0.9rem" }}>
            Are you sure you want to delete this user? This operation cannot be undone and will revoke all access permissions immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDelete(null)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error" sx={{ fontWeight: 700 }}>
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Confirm Bulk Delete ────────────────────────────────── */}
      <Dialog
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        PaperProps={{ sx: { borderRadius: "20px", p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700 }}>
          Delete {selected.length} Selected Accounts?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: brand.slate, fontSize: "0.9rem" }}>
            This will permanently remove all {selected.length} selected user accounts from the system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmBulkDelete(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleBulkDelete} variant="contained" color="error" sx={{ fontWeight: 700 }}>
            Delete All Selected
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default User;